import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RotateCcw, Send, Phone, Mail, AlertTriangle } from "lucide-react";
import { API, authHeaders } from "./shared";

const RebookingTab = () => {
  const [stats, setStats] = useState({ nudge_2day: 0, winback_14d: 0, winback_30d: 0, total: 0, sms_opted_out_count: 0, recent: [] });
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/rebooking/stats`, authHeaders());
      setStats(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load rebooking stats");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const runNow = async () => {
    setRunning(true);
    try {
      const res = await axios.post(`${API}/admin/rebooking/run-now`, {}, authHeaders());
      toast.success(`Sweep complete — ${res.data.total_rebooking_messages_sent} total messages sent to date`);
      await fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Run failed");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="rebooking-tab">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="font-serif text-2xl text-brand-text">Rebooking Automation</h2>
          <p className="font-sans text-sm text-brand-text-muted mt-1">
            2-day nudge for abandoned leads · 14-day touch-up for cornrows · 30-day winback for every other braid.
          </p>
        </div>
        <button
          onClick={runNow}
          disabled={running}
          data-testid="rebooking-run-now-btn"
          className="flex items-center gap-2 px-4 py-2.5 bg-brand-gold text-black font-sans text-sm font-semibold hover:bg-brand-gold/90 transition-colors disabled:opacity-50"
        >
          <RotateCcw size={14} className={running ? "animate-spin" : ""} />
          {running ? "Running…" : "Run Sweep Now"}
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Abandoned Nudges (2d)" value={stats.nudge_2day} icon={Send} testid="stat-nudge-2day" />
        <StatCard label="Cornrow Winbacks (14d)" value={stats.winback_14d} icon={Phone} testid="stat-winback-14d" />
        <StatCard label="Braid Winbacks (30d)" value={stats.winback_30d} icon={Mail} testid="stat-winback-30d" />
        <StatCard label="SMS Opted Out" value={stats.sms_opted_out_count} icon={AlertTriangle} testid="stat-opted-out" tone="warn" />
      </div>

      <div className="border border-brand-border bg-brand-surface p-5">
        <h3 className="font-serif text-lg text-brand-text mb-4">Recent Messages</h3>
        {loading ? (
          <p className="font-sans text-sm text-brand-text-muted">Loading…</p>
        ) : stats.recent.length === 0 ? (
          <p className="font-sans text-sm text-brand-text-muted">No rebooking messages sent yet. They'll appear here as the background loop fires them.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="recent-rebookings-table">
              <thead>
                <tr className="text-left text-brand-text-muted border-b border-brand-border">
                  <th className="py-2 pr-3 font-sans font-semibold uppercase text-[11px] tracking-wider">Booking ID</th>
                  <th className="py-2 pr-3 font-sans font-semibold uppercase text-[11px] tracking-wider">Kind</th>
                  <th className="py-2 pr-3 font-sans font-semibold uppercase text-[11px] tracking-wider">Channel</th>
                  <th className="py-2 pr-3 font-sans font-semibold uppercase text-[11px] tracking-wider">Sent</th>
                </tr>
              </thead>
              <tbody className="text-brand-text-muted">
                {stats.recent.map((r, i) => (
                  <tr key={`${r.booking_id || "row"}-${r.kind}-${r.sent_at || i}`} className="border-b border-brand-border/40">
                    <td className="py-2 pr-3 font-mono text-xs">{r.booking_id?.slice(0, 12) || "—"}</td>
                    <td className="py-2 pr-3"><span className="text-brand-gold">{r.kind}</span></td>
                    <td className="py-2 pr-3">{r.channel || "—"}</td>
                    <td className="py-2 pr-3 text-xs">{r.sent_at ? new Date(r.sent_at).toLocaleString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="border border-brand-border/60 bg-brand-surface/50 p-5">
        <h3 className="font-serif text-base text-brand-text mb-3">How it works</h3>
        <ul className="font-sans text-sm text-brand-text-muted leading-relaxed list-disc pl-5 space-y-1.5">
          <li><span className="text-brand-text">2-day nudge:</span> if a client filled the booking form but never paid the $25 deposit, an SMS + email goes out 2 days after to invite them back.</li>
          <li><span className="text-brand-text">14-day cornrow touch-up:</span> any cornrow booking automatically gets a winback 14 days after the appointment date.</li>
          <li><span className="text-brand-text">30-day winback:</span> every other braid style (box, knotless, twists, faux locs…) gets a winback 30 days after the appointment.</li>
          <li><span className="text-brand-text">Compliance:</span> every marketing SMS includes "Reply STOP to opt out." and opted-out numbers are auto-suppressed.</li>
          <li>Loop runs every 2 minutes — duplicates are blocked at the database level.</li>
        </ul>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, icon: Icon, testid, tone }) => (
  <div className={`border ${tone === "warn" ? "border-red-500/30 bg-red-500/5" : "border-brand-border bg-brand-surface"} p-4`} data-testid={testid}>
    <div className="flex items-center justify-between text-brand-text-muted">
      <span className="font-sans text-[11px] uppercase tracking-wider">{label}</span>
      <Icon size={14} className={tone === "warn" ? "text-red-400" : "text-brand-gold"} />
    </div>
    <div className={`mt-2 font-serif text-3xl ${tone === "warn" ? "text-red-300" : "text-brand-gold"}`}>{value}</div>
  </div>
);

export default RebookingTab;
